import { useState } from 'react'
import Title from 'components/ui/Title'
import { setReportingUser } from 'utils/sentry'
import { useApolloClient } from '@apollo/client'
import getMutationError from 'utils/getMutationError'
import { cn } from 'cn'
import VerifyEmail from 'components/pages/ProfilePage/EmailForm/VerifyEmail'
import EmailConfirmationCodeForm from 'components/pages/ProfilePage/EmailForm/EmailConfirmationCodeForm'
import Button from 'components/ui/Button'
import EmailPasswordForm from 'components/pages/ProfilePage/EmailForm/EmailPasswordForm'
import { logEvent, setAdsUserData } from 'utils/analytics'
import useToast from 'hooks/useToast'

import {
  useCustomerEmailCodeLogin,
  CustomerEmailCodeLogin
} from './types/CustomerEmailCodeLogin'
import EmailSignupForm from './EmailSignupForm'

type Customer = NonNullable<
  NonNullable<CustomerEmailCodeLogin['customerEmailCodeLogin']>['customer']
>

type Props = {
  onLogin: (customer: Customer, redirecTo: string | null) => void
  signup?: boolean
  catering?: boolean
  formOnly?: boolean
  subscribeToLotId: number | null
}

function EmailLoginForm({
  onLogin,
  signup = true,
  catering = false,
  formOnly = false,
  subscribeToLotId
}: Props) {
  const openToast = useToast()
  const client = useApolloClient()
  const [email, setEmail] = useState('')
  const [emailToken, setEmailToken] = useState<string | null>(null)
  const [verifying, setVerifying] = useState(false)
  const [customerEmailCodeLogin] = useCustomerEmailCodeLogin({})
  const [showSignup, setShowSignup] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [canUsePasswordAuth, setCanUsePasswordAuth] = useState<boolean>(false)
  const [usePasswordAuth, setUsePasswordAuth] = useState<boolean>(false)

  const afterCustomerLogin = async (
    customer: Customer,
    redirectTo: string | null,
    newUser: boolean
  ) => {
    setReportingUser(customer)
    await client.resetStore()
    onLogin(customer, redirectTo)
    await setAdsUserData(customer)
    if (newUser) {
      logEvent('sign_up', {
        method: 'phone'
      })
      // sign_up_ads
      logEvent('conversion', {
        send_to: 'AW-768772864/XyAZCISZoIkZEICWyu4C'
      })
    } else {
      logEvent('login', {
        method: 'phone'
      })
    }
  }

  const onVerificationSent = async (newPhone: string) => {
    setEmail(newPhone)
    setVerifying(true)
    setFormError(null)
  }

  const onChangeEmail = () => {
    setVerifying(false)
    setShowSignup(false)
    setEmailToken(null)
    setFormError(null)
  }

  const onEmailToken = async (token: string) => {
    setVerifying(false)
    setFormError(null)
    setEmailToken(token)
    const response = await customerEmailCodeLogin({
      variables: { emailToken: token }
    })
    const error = getMutationError(response!)
    if (error) {
      setFormError(error)
      return null
    }
    const { customer, redirectTo } = response!.data!.customerEmailCodeLogin!
    if (customer) {
      setFormError(null)
      await afterCustomerLogin(customer, redirectTo, false)
      if (subscribeToLotId) {
        openToast({
          title: 'Subscribed',
          message:
            "You already have an account with us, but we've subscribed you to the updates.",
          variant: 'success'
        })
      }
    } else if (signup) {
      setFormError(null)
      setShowSignup(true)
    } else {
      setFormError('No user with that phone exists')
    }
    return null
  }
  const onPasswordLogin = async (
    customer: Customer,
    redirectTo: string | null
  ) => {
    setReportingUser(customer)
    await client.resetStore()
    onLogin(customer, redirectTo)
  }

  return (
    <div className={cn({ 'bg-white md:mx-0 p-6 mx-4 rounded-xl': !formOnly })}>
      {!formOnly && (
        <Title variant="sectionTitle">
          {catering ? (
            'Contact Information'
          ) : (
            <>
              Sign in
              {signup && ' / Sign Up'}
            </>
          )}
        </Title>
      )}
      {formError && <p className="text-red-500 mb-4 italic">{formError}</p>}
      <VerifyEmail
        buttonName="Next"
        catering={catering}
        onChangeEmail={onChangeEmail}
        onVerificationSent={onVerificationSent}
        setCanUsePasswordAuth={setCanUsePasswordAuth}
        verified={showSignup}
        verifying={verifying}
      />
      {verifying && !usePasswordAuth && (
        <EmailConfirmationCodeForm email={email} onSuccess={onEmailToken} />
      )}
      {verifying && usePasswordAuth && (
        <EmailPasswordForm email={email} onSuccess={onPasswordLogin} />
      )}
      {verifying && canUsePasswordAuth && (
        <div>
          <Button
            color="default"
            onClick={() => setUsePasswordAuth(!usePasswordAuth)}
            variant="anchor"
          >
            {usePasswordAuth
              ? 'Use code sent to the email'
              : 'Use password instead'}
          </Button>
        </div>
      )}
      {showSignup && emailToken && (
        <EmailSignupForm
          catering={catering}
          emailToken={emailToken}
          onSuccess={afterCustomerLogin}
          subscribeToLotId={subscribeToLotId}
        />
      )}
    </div>
  )
}

export default EmailLoginForm
